// Sector page content, keyed by industry slug (extends content.industries; content.ts stays the source of truth
// for name, short, hero, sub, pains, wins, image, dppNote).
// Rules: sector language only from well-known public terms. No invented statistics. Fictional brands only in mockups.

import { DEFAULT_UNIT, type ExplainerUnit } from '../../explainers';

export type ExplainerKey = 'tag' | 'handover' | 'owner' | 'transfer' | 'erp' | 'claim';

export type UnitMock = {
  brand: string; // fictional
  brandColor: string;
  brandInk: string; // text colour on brandColor
  accent: string;
  product: string;
  kind: string;
  serial: string;
  status: { label: string; tone: 'ok' | 'due' };
  owner: { k: string; v: string }[];
  records: { k: string; v: string; tag?: string }[];
  actions: [string, string, string];
  /** Catalogue shot shown in the phone header; hidden if the file is missing. */
  photo?: string;
};

export type SectorPage = {
  seoTitle: string;
  seoDesc: string;
  h1: [string, string, string]; // [before, swipe phrase, after]
  channel: string; // who sits between you and the owner, in their words
  painsIntro: string;
  pains: { said: string; d: string }[];
  unit: UnitMock;
  unitCaption: string;
  steps: { k: string; t: string; d: string; x: ExplainerKey }[];
  roles: { who: string; before: string; after: string }[];
  compliance: { t: string; d: string; decided: string[]; open: string[] };
  posts: { slug: string; title: string }[];
  forward: string; // one-line reason the forwarder gives
  photos: { src: string; alt: string; fallback?: string }[]; // [0] tile + pains column, [1] editorial band; fallback while a generated shot is missing
  chip: { t: string; s: string }; // UI moment overlaid on the tile photo
  /** The paper form this sector signs today, used on /platform/forms. Optional so other pages are unaffected. */
  formExample?: { t: string; who: string };
};

export const sectorPages: Record<string, SectorPage> = {
  'agricultural-machinery': {
    seoTitle: 'Agricultural machinery warranty registration, PDI and dealer handover',
    seoDesc:
      'Dealer PDI and handover signed on a phone, warranty from the day the machine goes to work, and serial-exact parts before harvest stops. For agricultural machinery manufacturers.',
    h1: ['Ready for ', 'harvest.', ''],
    channel: 'dealer',
    painsIntro: 'If you sell through dealers, you will know these.',
    pains: [
      { said: 'The PDI sheet is still in the dealer’s van.', d: 'The paper turns up weeks later, so warranty starts on whatever date someone can prove.' },
      { said: 'Harvest stops for a part number.', d: 'The dealer rings you, and someone reads a serial plate through the dust.' },
      { said: 'The machine in the dealer yard has no story.', d: 'Traded in and sold on, with no service history or warranty status.' },
    ],
    unit: {
      brand: 'Harrowfield',
      brandColor: '#2E4A3E',
      brandInk: '#fff',
      accent: '#D4A045',
      product: 'RB160 Round Baler',
      kind: 'Round baler',
      serial: 'HF-RB160-24-0417',
      status: { label: 'Warranty active', tone: 'ok' },
      owner: [
        { k: 'Dealer', v: 'Tarnside Agri, Hexham' },
        { k: 'Handover', v: '12 Jul, PDI signed' },
      ],
      records: [
        { k: 'Operator manual', v: 'Build 2024, rev C' },
        { k: 'Parts for this serial', v: 'Pick-up tines, net wrap, belts' },
        { k: 'Service bulletin', v: 'Knotter check before season', tag: 'New' },
      ],
      actions: ['Order a part', 'Book a service', 'Report a problem'],
    },
    unitCaption: 'Illustrative. Harrowfield is a fictional brand.',
    steps: [
      { k: 'In the yard', t: 'PDI and handover on the dealer’s phone', d: 'The dealer works through your checklist and the farmer signs. Warranty starts that day.', x: 'handover' },
      { k: 'In the field', t: 'The operator scans for the right part', d: 'The manual for that build and the parts that fit, ordered through the selling dealer.', x: 'owner' },
      { k: 'At trade-in', t: 'The history goes with the machine', d: 'The next owner takes over the record with consent, service history intact.', x: 'transfer' },
    ],
    roles: [
      { who: 'Aftersales', before: 'Chasing PDI forms and reading serials off photos.', after: 'Claims arrive with the serial, handover date and dealer.' },
      { who: 'Your dealers', before: 'Fax, email and another portal login.', after: 'Credited at registration, with parts and service routed to them.' },
      { who: 'The MD', before: 'No idea who owns which machine.', after: 'Every machine you have sold, by dealer and owner.' },
    ],
    compliance: {
      t: 'Digital manuals from 2027. No passport date yet.',
      d: 'The Machinery Regulation is the nearer change, and the same code covers both.',
      decided: ['From 20 January 2027, Machinery Regulation (EU) 2023/1230 allows digital instructions, kept online for at least 10 years', 'Tractors fall under Regulation (EU) No 167/2013; mounted machinery falls under the Machinery Regulation', 'Battery passports apply from 18 February 2027 to EV, light means of transport and industrial batteries over 2 kWh'],
      open: ['No passport date has been set for agricultural machinery itself'],
    },
    posts: [
      { slug: 'john-deere-right-to-repair-settlement-agricultural', title: 'John Deere’s right-to-repair settlement: what it means for you' },
      { slug: 'blog-garden-tractor-warranty-spare-parts', title: 'Your spare parts revenue is going to Amazon' },
      { slug: 'b2b-equipment-resale-digital-identity', title: 'B2B equipment resale: digital identity changes everything' },
    ],
    forward: 'how we could take PDI forms off paper and keep our dealers in the loop',
    photos: [{ src: '/images/v2/gen/agri-handover.jpg', alt: 'A dealer and a farmer walking past a new round baler in a dealer yard' }, { src: '/images/v2/gen/agri-workshop-b.jpg', alt: 'Two technicians working on a baler in a farm workshop' }],
    chip: { t: 'PDI signed in the yard', s: 'Warranty active from today' },
    formExample: { t: 'Pre-delivery inspection', who: 'Dealer and farmer, in the yard' },
  },

  'lifting-access-equipment': {
    seoTitle: 'LOLER and PUWER inspection records per serial for lifting and access equipment',
    seoDesc:
      'Thorough examinations, service visits and certificates attached to each serial, visible to the next inspector, owner or hirer with one scan. For lifting and access equipment manufacturers.',
    h1: ['The record ', 'travels with it.', ''],
    channel: 'hirer or service agent',
    painsIntro: 'The paperwork rarely keeps up with the machine.',
    pains: [
      { said: 'The thorough examination report is in somebody else’s filing cabinet.', d: 'Under LOLER the report goes to whoever had the machine that day.' },
      { said: 'Re-hired, resold, and the certificate chain breaks.', d: 'The next owner starts from nothing, or a scan of a scan.' },
      { said: 'The pre-use check sheet lives in the cab.', d: 'Proving who inspected it means finding the right piece of paper.' },
    ],
    unit: {
      brand: 'Talbrook',
      brandColor: '#2D2D2D',
      brandInk: '#fff',
      accent: '#D4A045',
      product: 'TX12 Scissor Lift',
      kind: 'Scissor lift, MEWP',
      serial: 'TB-TX12-2023-1186',
      status: { label: 'Examination in date', tone: 'ok' },
      owner: [
        { k: 'Held by', v: 'Northgate Hire, Leeds depot' },
        { k: 'Next exam due', v: '14 Mar' },
      ],
      records: [
        { k: 'Thorough examination', v: 'Signed by competent person', tag: 'PDF' },
        { k: 'Service visit', v: 'Hydraulics, emergency lowering' },
        { k: 'Safety notice', v: 'None open for this serial' },
      ],
      actions: ['View certificates', 'Log a pre-use check', 'Report a defect'],
    },
    unitCaption: 'Illustrative. Talbrook is a fictional brand.',
    steps: [
      { k: 'Examine', t: 'Inspection signed on site, filed on the serial', d: 'The engineer completes your form on a phone and signs. The sealed report lands on that unit.', x: 'handover' },
      { k: 'Hand on', t: 'The record moves when the machine does', d: 'Re-hire, resale or a new depot: the next holder takes over the record.', x: 'transfer' },
      { k: 'Act', t: 'Defects and notices reach the right serial', d: 'A defect becomes a case on that unit. Notices target the people holding it.', x: 'claim' },
    ],
    roles: [
      { who: 'Service & inspection', before: 'Reports scattered across hirers, agents and inboxes.', after: 'One history per serial, one scan for the next competent person.' },
      { who: 'Hirers & agents', before: 'Re-keying certificates and chasing old reports at resale.', after: 'Forms signed and sealed on a phone.' },
      { who: 'The MD', before: 'Exposure you cannot size when a notice goes out.', after: 'Where every affected serial is, and who holds it.' },
    ],
    compliance: {
      t: 'We keep the record. The duty stays where the law puts it.',
      d: 'LOLER and PUWER duties sit with owners, employers and competent persons. We do not replace the examination.',
      decided: ['From 20 January 2027, Machinery Regulation (EU) 2023/1230 covers lifting accessories, chains, ropes and webbing, and allows digital instructions'],
      open: ['Lifting and access equipment is not on the 2025 to 2030 passport working plan'],
    },
    posts: [
      { slug: 'equipment-hire-digital-identity', title: 'Equipment hire needs a digital identity layer' },
      { slug: 'b2b-equipment-resale-digital-identity', title: 'B2B equipment resale: digital identity changes everything' },
      { slug: 'product-recalls-broken-digital-identity-fix', title: 'Product recalls are broken. Digital identity narrows the gap' },
    ],
    forward: 'how we could keep LOLER records on each serial instead of in binders',
    photos: [{ src: '/images/v2/gen/lifting-inspection.jpg', alt: 'An engineer in a hi-vis vest inspecting a scissor lift with a phone' }],
    chip: { t: 'Thorough examination signed', s: 'Filed on serial TB-TX12-1186' },
    formExample: { t: 'Thorough examination report', who: 'Competent person, on site' },
  },

  'heating-heat-pumps': {
    seoTitle: 'Heat pump and boiler warranty registration and commissioning records',
    seoDesc:
      'Installers register and commission on site, homeowners get the right manual and a service reminder, and you see every unit in every home. For heating and heat pump manufacturers.',
    h1: ['Warranty from ', 'commissioning.', ''],
    channel: 'installer',
    painsIntro: 'Between you and the homeowner: an installer, a van and three forms.',
    pains: [
      { said: 'Registration depends on the installer remembering.', d: 'The van has gone, and your registration card is still in the plant room.' },
      { said: 'The commissioning sheet is a photo on someone’s phone.', d: 'Benchmark, MCS, your warranty form: three places, none on the unit.' },
      { said: 'The longer warranty needed a service nobody booked.', d: 'Nobody reminded the homeowner, and the claim becomes an argument.' },
    ],
    unit: {
      brand: 'Halvard',
      brandColor: '#F7F5F0',
      brandInk: '#2D2D2D',
      accent: '#2E4A3E',
      product: 'Halvard AW-9',
      kind: 'Air source heat pump',
      serial: 'KAW9-26-04182',
      status: { label: 'Commissioned', tone: 'ok' },
      owner: [
        { k: 'Installer', v: 'Brightwell Heating' },
        { k: 'Next service', v: 'Due in 3 months' },
      ],
      records: [
        { k: 'Commissioning record', v: 'Signed on site, photos attached', tag: 'Sealed' },
        { k: 'Homeowner manual', v: 'For this model and controls' },
        { k: 'Warranty', v: 'Extended cover, service-linked' },
      ],
      actions: ['Book annual service', 'Ask a question', 'Report a fault'],
    },
    unitCaption: 'Illustrative. Halvard is a fictional brand.',
    steps: [
      { k: 'Commission', t: 'The installer registers it before leaving site', d: 'Scan, fill in your commissioning form, add photos, sign. Warranty starts that day, with proof.', x: 'handover' },
      { k: 'Live with it', t: 'The homeowner scans for help', d: 'The right manual, answers from your own documents, and a service reminder under your brand.', x: 'owner' },
      { k: 'Claim', t: 'Claims arrive with the install record', d: 'The case opens on that serial with commissioning, installer and service history attached.', x: 'claim' },
    ],
    roles: [
      { who: 'Technical & warranty', before: 'Claims you cannot judge without the install details.', after: 'Commissioning, photos and service history on every claim.' },
      { who: 'Your installers', before: 'Another portal, another password, another form.', after: 'Scan, fill, sign. Credited for the install, sent the service work.' },
      { who: 'The MD', before: 'No view of which homes your units are in.', after: 'Every unit, every installer, every home, under your own brand.' },
    ],
    compliance: {
      t: 'Energy label now, passport not yet scheduled',
      d: 'Heaters already carry an energy label and an EPREL entry. The gain today is commissioning, warranty and service on one code.',
      decided: ['Heaters are covered by existing ecodesign and energy labelling rules, with model data registered in EPREL', 'Heat pumps, space heaters and air conditioners are not on the 2025 to 2030 passport working plan'],
      open: ['Revised ecodesign rules for space heaters are in draft, with a QR code on the energy label linking to EPREL'],
    },
    posts: [
      { slug: 'blog-hvac-manufacturers-digital-product-identity', title: 'How HVAC manufacturers can use digital product identity' },
      { slug: 'warranty-registration-benefits', title: 'Why almost nobody registers your product' },
      { slug: 'blog-espr-ecodesign-sustainable-products-regulation', title: 'ESPR explained: what manufacturers need to know' },
    ],
    forward: 'how installers could register and commission every unit on site',
    photos: [{ src: '/images/v2/gen/heatpump-commission-b.jpg', alt: 'An installer kneeling beside an outdoor heat pump unit in a garden' }, { src: '/images/v2/gen/hp-house-dusk.jpg', alt: 'A brick house at dusk with an air source heat pump beside the front garden' }],
    chip: { t: 'Commissioned on site', s: 'Installer signed, photos attached' },
    formExample: { t: 'Commissioning certificate', who: 'Installer and homeowner' },
  },

  'commercial-kitchen': {
    seoTitle: 'Commercial kitchen equipment service history and warranty per appliance',
    seoDesc:
      'Gas safety, servicing and parts history attached to each appliance, so the next operator and the next engineer start from the facts. For commercial catering equipment manufacturers.',
    h1: ['Give every appliance', 'a memory.', ''],
    channel: 'distributor or service agent',
    painsIntro: 'If you sell through distributors and service agents, you will know these.',
    pains: [
      { said: 'The service log left with the old operator.', d: 'The site is sold, and the paper log and gas safety records go with the people.' },
      { said: 'The engineer arrives without the model history.', d: 'First visit, wrong part on the van, second visit.' },
      { said: '“The door seal for the big one.”', d: 'Ordered by description, with a fifty-fifty chance it fits.' },
    ],
    unit: {
      brand: 'Fornell',
      brandColor: '#2D2D2D',
      brandInk: '#fff',
      accent: '#D4A045',
      product: 'CX10 Combi Oven',
      kind: 'Combination oven, 10 grid',
      serial: 'FN-CX10-24-55021',
      status: { label: 'Service due', tone: 'due' },
      owner: [
        { k: 'Operator', v: 'The Corner Kitchen' },
        { k: 'Service agent', v: 'Southern Catering Services' },
      ],
      records: [
        { k: 'Service history', v: 'Visits, parts and engineer notes' },
        { k: 'Gas safety record', v: 'Latest check attached', tag: 'PDF' },
        { k: 'Parts for this serial', v: 'Door seal, probe, fan motor' },
      ],
      actions: ['Request a visit', 'Order a part', 'Error code help'],
    },
    unitCaption: 'Illustrative. Fornell is a fictional brand.',
    steps: [
      { k: 'Engineer', t: 'The engineer scans before opening the panel', d: 'Model, history and the right parts, before the first screw comes out.', x: 'owner' },
      { k: 'Fault', t: 'A fault becomes a case, not a phone call', d: 'Staff scan and enter the error code. The case goes to your service agent with the history.', x: 'claim' },
      { k: 'New operator', t: 'When the site changes hands, the record stays', d: 'The new operator takes over the record, gas safety history intact.', x: 'transfer' },
    ],
    roles: [
      { who: 'Service', before: 'Engineers driving to sites blind.', after: 'Model, history and parts known before the visit.' },
      { who: 'Distributors & agents', before: 'Phone, email and spreadsheets for every call-out.', after: 'Cases routed to them with the serial and history.' },
      { who: 'The MD', before: 'Parts revenue leaking with every new operator.', after: 'Every appliance accounted for, whoever runs the kitchen this year.' },
    ],
    compliance: {
      t: 'No passport due. Records on demand.',
      d: 'Kitchens are inspected and insured on the strength of the record. We keep it on the appliance, through every change of operator.',
      decided: ['From 20 January 2027, Machinery Regulation (EU) 2023/1230 covers powered kitchen machinery, such as mixers and rack ovens, and allows digital instructions', 'Gas appliances are covered by Regulation (EU) 2016/426', 'Professional cooking equipment has no EU ecodesign rules'],
      open: ['No Digital Product Passport date has been set for commercial catering equipment; professional dishwashers are on the working plan for 2026'],
    },
    posts: [
      { slug: 'serialized-vs-blanket-warranty', title: 'Serialised vs blanket warranty: why per-unit tracking changes everything' },
      { slug: 'product-manual-liability-static-pdfs-fail', title: 'Your product manual is a liability' },
      { slug: 'b2b-equipment-resale-digital-identity', title: 'B2B equipment resale: digital identity changes everything' },
    ],
    forward: 'how the service history could stay with each appliance when sites change hands',
    photos: [{ src: '/images/v2/gen/kitchen-service.jpg', alt: 'A service engineer opening the side panel of a combination oven in a commercial kitchen' }],
    chip: { t: 'Service history on the appliance', s: 'Kept through a change of operator' },
    formExample: { t: 'Gas safety and service record', who: 'Service engineer and site manager' },
  },

  'industrial-precision': {
    seoTitle: 'Lot traceability and scannable packing slips from your ERP',
    seoDesc:
      'Packing slips, lots and certificates from your ERP become a scannable record your customer can act on. Delivery problems become cases, not email chains. Passport-ready lot data.',
    h1: ['Every lot, ', 'traceable.', ''],
    channel: 'customer’s goods-in team',
    painsIntro: 'If your customers report delivery problems by email, you will know these.',
    pains: [
      { said: 'A delivery goes wrong and the fix runs on email.', d: 'Photos, forwards and calls, with nobody sure what shipped.' },
      { said: 'The 3.1 certificate travels separately.', d: 'Goods-in cannot match it to the lot on the bench.' },
      { said: 'The traceability is in your ERP. Your customer cannot see it.', d: 'Heat numbers, lots, serials, shared by hand when someone asks.' },
    ],
    unit: {
      brand: 'Lotwerk',
      brandColor: '#1F3B57',
      brandInk: '#fff',
      accent: '#E5893B',
      product: 'Delivery D-24118',
      kind: 'Packing slip, 3 lines',
      serial: 'LOT L-24-0918',
      status: { label: 'Delivered', tone: 'ok' },
      owner: [
        { k: 'Shipped to', v: 'Goods-in, Line 2' },
        { k: 'From ERP', v: 'Order, lots, serials' },
      ],
      records: [
        { k: 'Flange, 316L stainless', v: 'Qty 40, lot L-24-0918' },
        { k: 'Inspection certificate', v: 'EN 10204 3.1', tag: 'PDF' },
        { k: 'Material data', v: 'Alloy, heat number, origin' },
      ],
      actions: ['Something wrong?', 'Download certificates', 'Reorder'],
    },
    unitCaption: 'Illustrative. Lotwerk is a fictional company.',
    steps: [
      { k: 'Despatch', t: 'The packing slip you already print, now scannable', d: 'Your ERP creates the despatch as today. We add a code for that shipment, its lines, lots and serials.', x: 'tag' },
      { k: 'Connect', t: 'Your ERP stays the master', d: 'Parts, packing slips, serials and customers flow in. An approved return goes back as an RMA.', x: 'erp' },
      { k: 'Resolve', t: 'Something wrong becomes a managed case', d: 'Your customer scans, sees what shipped and reports it. The case arrives with order, lot and certificate.', x: 'claim' },
    ],
    roles: [
      { who: 'Quality & customer service', before: 'Rebuilding what shipped from emails and photos.', after: 'Every report tied to the order, lot and certificate.' },
      { who: 'Your customers', before: 'Chasing certificates and waiting for replies.', after: 'One scan for what shipped, the paperwork and a way to report.' },
      { who: 'The MD', before: 'Traceability as a cost you carry.', after: 'A connected delivery you can offer customers, ready for passports.' },
    ],
    compliance: {
      t: 'The steel passport is next',
      d: 'Aluminium is to follow steel. The code on your delivery note can carry the passport, from lot data you already hold.',
      decided: ['The Ecodesign for Sustainable Products Regulation (EU) 2024/1781 is in force', 'The Commission\'s ESPR working plan (April 2025) puts iron and steel first, with adoption indicated for Q4 2026', 'The public consultation on the iron and steel act closed in August 2026'],
      open: ['Data fields and dates for metals are not final. Requirements apply no earlier than 18 months after the act enters into force', 'Which parts made from steel are in scope will be set by the act'],
    },
    posts: [
      { slug: 'dpp-implementation-mid-market', title: 'DPP implementation without the enterprise budget' },
      { slug: 'manufacturer-guide-gs1-digital-link', title: 'The manufacturer’s guide to GS1 Digital Link' },
      { slug: 'digital-product-passport-uk-manufacturers', title: 'Digital Product Passport for UK manufacturers' },
    ],
    forward: 'how our packing slips could become scannable from the ERP we already run',
    photos: [{ src: '/images/v2/gen/precision-cnc-b.jpg', alt: 'A machinist measuring a turned steel flange beside a CNC machine' }, { src: '/images/v2/gen/precision-packing.jpg', alt: 'Gloved hands packing machined parts into a wooden case' }],
    chip: { t: 'Lot L-24-0918 shipped', s: '3.1 certificate attached' },
    formExample: { t: 'Certificate of conformity', who: 'Quality, at dispatch' },
  },

  'premium-durables': {
    seoTitle: 'Product registration and ownership transfer for premium durable goods',
    seoDesc:
      'Registration in seconds, the right manual and part for every vintage, and a direct line to second and third owners. For hi-fi, outdoor and caravan manufacturers.',
    h1: ['Made to last ', 'decades.', ''],
    channel: 'retailer',
    painsIntro: 'You meet almost none of the people who own your products.',
    pains: [
      { said: 'You only meet owners when something breaks.', d: 'The card went in the bin, and the first you hear is an angry email.' },
      { said: 'Your biggest fans bought second-hand.', d: 'A lively resale market, and you cannot see one owner in it.' },
      { said: 'The answer is on page 34 of a manual nobody has.', d: 'Calls about setup and spares the right document would answer.' },
    ],
    unit: {
      brand: 'Aldwyn Audio',
      brandColor: '#2D2D2D',
      brandInk: '#F7F5F0',
      accent: '#D4A045',
      product: 'A-40 Integrated Amplifier',
      kind: 'Integrated amplifier',
      serial: 'AA40-2019-07733',
      status: { label: 'Registered, 2nd owner', tone: 'ok' },
      owner: [
        { k: 'Owner since', v: 'March, transferred' },
        { k: 'Built', v: '2019, revision B' },
      ],
      records: [
        { k: 'Manual for your build', v: 'Revision B, with remote codes' },
        { k: 'Genuine parts', v: 'Volume encoder, feet, fuses' },
        { k: 'Service history', v: 'Kept from the first owner' },
      ],
      actions: ['Ask a question', 'Order a part', 'Book a service'],
    },
    unitCaption: 'Illustrative. Aldwyn Audio is a fictional brand.',
    steps: [
      { k: 'Register', t: 'Owners register by scanning, in their own time', d: 'No card, no app, no password. The warranty and the right manual straight away.', x: 'owner' },
      { k: 'Sell on', t: 'The second owner joins the record', d: 'The next owner takes over with consent and keeps the history. Now you know them too.', x: 'transfer' },
      { k: 'Help', t: 'Problems arrive with the details attached', d: 'Answers from your own documents. When a person is needed, the case opens with its history.', x: 'claim' },
    ],
    roles: [
      { who: 'Customer service', before: 'Repeat questions and claims with no serial.', after: 'Fewer manual questions, complete cases for the rest.' },
      { who: 'Your retailers', before: 'Handling registrations and returns for you.', after: 'Credited for the sale, with service and parts sent where you choose.' },
      { who: 'The MD', before: 'A customer list that ends at the first sale.', after: 'A direct line to every owner, including the second and third.' },
    ],
    compliance: {
      t: 'Repair rules now, passports later',
      d: 'Most premium durables have no passport date yet. On the call we will tell you plainly which rules apply to you.',
      decided: ['From 31 July 2026 the Right to Repair Directive (EU) 2024/1799 obliges makers to offer repair for products such as washing machines, fridges and displays', 'Battery passports apply from 18 February 2027 to e-bike and other light transport batteries, and to industrial batteries over 2 kWh, including home storage'],
      open: ['Electronics and furniture passport dates are not fixed; the working plan indicates repairability rules in 2027 and furniture in 2028'],
    },
    posts: [
      { slug: 'marine-boat-warranty-ownership-transfer', title: 'Marine warranty: what happens when you sell the boat?' },
      { slug: 'warranty-registration-benefits', title: 'Why almost nobody registers your product' },
      { slug: 'eu-right-to-repair-july-2026-uk-manufacturers', title: 'EU right to repair: what UK manufacturers must do' },
    ],
    forward: 'how we could meet the second and third owners of what we make',
    photos: [{ src: '/images/v2/gen/hifi-owner.jpg', alt: 'An owner turning the volume knob of an amplifier at home' }, { src: '/images/v2/gen/outdoor-caravan.jpg', alt: 'A couple setting up camp beside their caravan at sunset' }],
    chip: { t: 'Registered by scan', s: 'Second owner, history kept' },
    formExample: { t: 'Trade-in condition report', who: 'Retailer and owner' },
  },

  'medical-laboratory-instruments': {
    seoTitle: 'PPM, calibration records and field safety notices per serial for medical and laboratory instruments',
    seoDesc:
      'Planned maintenance and calibration signed on a phone and filed on the serial, notices that reach the lab running each unit today, and history that survives a move or a resale. For medical and laboratory instrument manufacturers.',
    h1: ['Every visit, ', 'on the serial.', ''],
    channel: 'distributor or service partner',
    painsIntro: 'The paperwork has to keep up with every serial.',
    pains: [
      { said: 'The calibration certificate is in the engineer’s inbox.', d: 'It went to whoever booked the visit, and the audit asks for it by serial.' },
      { said: 'A field safety notice, and a distributor’s customer list.', d: 'Reaching the lab that runs each serial today depends on someone else’s records.' },
      { said: 'Moved to another department, and the history stayed behind.', d: 'The label stays on the unit. The service record does not.' },
    ],
    unit: {
      brand: 'Brennock',
      brandColor: '#E8F0F3',
      brandInk: '#123047',
      accent: '#0F6E8C',
      product: 'CR-4 Benchtop Centrifuge',
      kind: 'Centrifuge, refrigerated',
      serial: 'BRK-CR4-25-01376',
      status: { label: 'Calibration in date', tone: 'ok' },
      owner: [
        { k: 'Site', v: 'Kingsmere Lab' },
        { k: 'Next PPM', v: '14 Feb' },
      ],
      records: [
        { k: 'PPM and calibration', v: 'Speed and temperature verified', tag: 'PDF' },
        { k: 'Field safety notice', v: 'None open for this serial' },
        { k: 'Parts for this serial', v: 'Lid seal, rotor, air filter' },
      ],
      actions: ['View certificates', 'Request a visit', 'Report a fault'],
      photo: '/images/v2/gen/unit-centrifuge-thumb.jpg',
    },
    unitCaption: 'Illustrative. Brennock is a fictional brand.',
    steps: [
      { k: 'Maintain', t: 'PPM and calibration signed at the bench', d: 'The engineer works through your PPM checklist on a phone, records the readings and signs. The certificate lands on that serial.', x: 'handover' },
      { k: 'Fault', t: 'A fault arrives with its history', d: 'The lab scans, and a case opens with PPM, calibration and parts history attached. Safety notices target affected serials the same way.', x: 'claim' },
      { k: 'Move on', t: 'The record goes with the instrument', d: 'Moved, or refurbished and sold on: the next owner takes over the record.', x: 'transfer' },
    ],
    roles: [
      { who: 'Service', before: 'PPM schedules in a spreadsheet, certificates in engineers’ inboxes.', after: 'Every visit, reading and certificate on the serial it belongs to.' },
      { who: 'Quality & regulatory', before: 'Rebuilding which serials went where.', after: 'Affected serials, current holders and acknowledgements in one place, to support your post-market surveillance.' },
      { who: 'Your distributors', before: 'Forwarding notices and keeping customer lists by hand.', after: 'Notices, service work and parts routed to them, and credited for the sale.' },
    ],
    compliance: {
      t: 'We keep the record. Your regulatory process stays yours.',
      d: 'The service record and chain of owners on each serial supports your post-market surveillance and field safety notice process.',
      decided: [
        'In Great Britain, new post-market surveillance requirements in the UK MDR 2002 have applied since 16 June 2025',
        'EU MDR (EU) 2017/745 and IVDR (EU) 2017/746 require a post-market surveillance system, unique device identification and field safety notices',
        'MHRA guidance says the manufacturer stays responsible for a field safety notice reaching end users, including when it goes through distributors',
      ],
      open: ['Mandatory UDI in Great Britain is in draft regulations and not yet in force', 'Medical devices are not on the Commission’s 2025 to 2030 passport working plan'],
    },
    posts: [
      { slug: 'product-recalls-broken-digital-identity-fix', title: 'Product recalls are broken. Digital identity narrows the gap' },
      { slug: 'blog-product-serialisation-practical-guide-manufacturers', title: 'Product serialisation: a practical guide for manufacturers' },
      { slug: 'b2b-equipment-resale-digital-identity', title: 'B2B equipment resale: digital identity changes everything' },
    ],
    forward: 'how PPM certificates and field safety notices could sit on each serial instead of in inboxes',
    photos: [
      { src: '/images/v2/gen/instr-ppm.jpg', alt: 'A service engineer checking a benchtop centrifuge in a hospital laboratory', fallback: '/images/v2/gen/unit-centrifuge.jpg' },
      { src: '/images/v2/gen/instr-freezer.jpg', alt: 'An engineer checking the temperature of a laboratory freezer with a reference probe' },
    ],
    chip: { t: 'Calibration signed at the bench', s: 'Filed on serial BRK-CR4-01376' },
    formExample: { t: 'PPM and calibration certificate', who: 'Service engineer and lab manager' },
  },

  'sports-fitness-equipment': {
    seoTitle: 'Fitness equipment service records, safety inspections and warranty registration per serial',
    seoDesc:
      'Service visits and safety inspections signed on site, error codes answered from your own manuals, serial-exact parts, and home buyers registered by scan. For commercial and home fitness equipment manufacturers.',
    h1: ['Back on the ', 'gym floor.', ''],
    channel: 'dealer or service partner',
    painsIntro: 'Between you and the user sit a dealer, an operator and an engineer.',
    pains: [
      { said: 'The treadmill is out of order and the engineer is guessing.', d: 'The error code is on a sticky note, and the right part is not on the van.' },
      { said: 'The service contract lives in a spreadsheet.', d: 'Nobody can say which machines were serviced, when, or by whom.' },
      { said: 'The inspection sheet is in the gym manager’s drawer.', d: 'Proving the check was done, on which machine, means finding the paper.' },
    ],
    unit: {
      brand: 'Strideworth',
      brandColor: '#D4462A',
      brandInk: '#fff',
      accent: '#1E2228',
      product: 'T9 Commercial Treadmill',
      kind: 'Treadmill, commercial',
      serial: 'SW-T9-24-20871',
      status: { label: 'Service due', tone: 'due' },
      owner: [
        { k: 'Site', v: 'Harbourside' },
        { k: 'Service partner', v: 'Kellaway' },
      ],
      records: [
        { k: 'Service history', v: 'Visits, parts and engineer notes' },
        { k: 'Safety inspection', v: 'Latest check signed', tag: 'PDF' },
        { k: 'Parts for this serial', v: 'Running belt, deck, drive motor' },
      ],
      actions: ['Report a fault', 'Error code help', 'Order a part'],
      photo: '/images/v2/gen/unit-treadmill-thumb.jpg',
    },
    unitCaption: 'Illustrative. Strideworth is a fictional brand.',
    steps: [
      { k: 'Service', t: 'The engineer scans before the hood comes off', d: 'History and the parts that fit, on a phone. The visit and safety checks are signed on the gym floor.', x: 'handover' },
      { k: 'Error code', t: 'Gym staff scan for the answer', d: 'The answer comes from your own manual. If it needs an engineer, a case opens with the history.', x: 'owner' },
      { k: 'Resell', t: 'Used kit keeps its history', d: 'When a gym refits and sells on, the next owner takes over the record.', x: 'transfer' },
    ],
    roles: [
      { who: 'Service', before: 'Engineers arriving without the model history or the right part.', after: 'History, error codes and serial-exact parts known before the visit.' },
      { who: 'Dealers & operators', before: 'Inspection sheets kept by hand, site by site.', after: 'Every visit and inspection signed on site and visible per machine.' },
      { who: 'Customer service', before: 'Home buyers who never registered, calling with no serial.', after: 'Buyers register by scan, and fault reports arrive with the serial attached.' },
    ],
    compliance: {
      t: 'Safety rules now, no passport due',
      d: 'The rules that matter are machinery and product safety. What counts is the record, and reaching the owner.',
      decided: [
        'From 20 January 2027, Machinery Regulation (EU) 2023/1230 covers powered machinery and allows digital instructions, kept online for at least 10 years',
        'The General Product Safety Regulation (EU) 2023/988 has applied since 13 December 2024. Recalls must reach affected consumers directly where possible, and registration schemes must let buyers give contact details for safety use only',
      ],
      open: ['Fitness equipment is not on the 2025 to 2030 passport working plan'],
    },
    posts: [
      { slug: 'warranty-registration-benefits', title: 'Why almost nobody registers your product' },
      { slug: 'b2b-equipment-resale-digital-identity', title: 'B2B equipment resale: digital identity changes everything' },
      { slug: 'product-manual-liability-static-pdfs-fail', title: 'Your product manual is a liability' },
    ],
    forward: 'how service visits and inspections could sit on each machine, for gyms and home buyers alike',
    photos: [
      { src: '/images/v2/gen/fitness-hotel.jpg', alt: 'A technician servicing a treadmill in a hotel gym early in the morning', fallback: '/images/v2/gen/fitness-service-b.jpg' },
      { src: '/images/v2/gen/fitness-cable.jpg', alt: 'A gym manager checking a frayed cable on a lat pulldown that has been taped off' },
    ],
    chip: { t: 'Service visit signed', s: 'Filed on serial SW-T9-20871' },
    formExample: { t: 'Service and safety inspection', who: 'Engineer and gym manager' },
  },
};

/** The unit each sector's explainers tell their story about. All names are fictional. */
export const sectorUnits: Record<string, ExplainerUnit> = {
  'agricultural-machinery': {
    brand: 'Harrowfield',
    model: 'RB160',
    kind: 'Round baler',
    serial: 'HF-RB160-24-0417',
    lot: 'L2407-12',
    partner: 'Tarnside Agri',
    partnerRole: 'dealer',
    owner: 'Hill Farm',
    owner2: 'Ridge Contracting',
    checks: ['Hydraulics checked', 'PTO guard fitted', 'Tyre pressures set', 'Safety decals in place', 'Operator walkthrough'],
    form: 'Pre-delivery inspection',
    doneNote: 'PDI 5 of 5 · signed',
    question: 'Which oil does the gearbox take, and how much?',
    answer: '80W-90 gear oil, 2.4 litres. Check the level every 50 hours at the plug on the left side.',
    source: 'Operator manual p.42',
    section: '7.3 Gearbox oil',
    excerpt: 'Use 80W-90 gear oil, 2.4 L. Check every 50 hours.',
    part: 'Net wrap unit',
    issue: 'Net wrap not cutting cleanly',
    parts: [
      ['Pick-up tine set', 'PTS-24-1182', 'L2407-03'],
      ['Net wrap unit', 'NWU-24-0310', 'L2407-08'],
      ['Belt set', 'BLT-24-0921', 'L2406-77'],
    ],
    history: [
      ['Handover · Tarnside Agri', 'JUL 2024'],
      ['Service · 50 hours', 'SEP 2024'],
      ['Service · 250 hours', 'JUN 2025'],
    ],
  },
  'lifting-access-equipment': {
    brand: 'Talbrook',
    model: 'TX12',
    kind: 'Scissor lift',
    serial: 'TB-TX12-2023-1186',
    lot: 'L2311-04',
    partner: 'Pennine Lift Services',
    partnerRole: 'service agent',
    owner: 'Northgate Hire',
    owner2: 'Calder Access Hire',
    checks: ['Structure and welds', 'Hydraulics and hoses', 'Emergency lowering', 'Overload sensing', 'Guardrails and gate'],
    form: 'Thorough examination',
    signLabel: 'Competent person',
    signer: 'Pennine Lift Services',
    outcome: ['Examination filed', 'on this serial'],
    statusRow: ['Examination', 'Due', 'In date until March'],
    doneNote: 'Exam 5 of 5 · signed',
    question: 'How do I lower the platform in an emergency?',
    answer: 'Use the emergency lowering valve at the rear of the chassis. Pull and hold it until the platform is fully down.',
    source: 'Operator manual p.27',
    section: '4.6 Emergency lowering',
    excerpt: 'Pull and hold the lowering valve at the rear.',
    part: 'Pothole guard',
    issue: 'Pothole guard not deploying',
    parts: [
      ['Pothole guard', 'TB-PG-23-0412', 'L2310-02'],
      ['Lowering valve', 'TB-LV-23-0920', 'L2309-11'],
      ['Platform gate', 'TB-GT-23-0188', 'L2311-06'],
    ],
    history: [
      ['Examination · 6 months', 'MAR 2025'],
      ['Service · hydraulics', 'JUN 2025'],
      ['Examination · 6 months', 'SEP 2025'],
    ],
    serviceNote: '2 examinations, both in date',
    since: 'In date, signed and sealed',
  },
  // The site's default story is the heat pump; the heating page tells the same one.
  'heating-heat-pumps': DEFAULT_UNIT,
  'commercial-kitchen': {
    brand: 'Fornell',
    model: 'CX10',
    kind: 'Combi oven',
    serial: 'FN-CX10-24-55021',
    lot: 'L24-0412',
    partner: 'Southern Catering Services',
    partnerRole: 'service agent',
    owner: 'The Corner Kitchen',
    owner2: 'Harbour Street Café',
    checks: ['Gas supply and pressure', 'Flue and extraction', 'Water and drain connected', 'Probe and controls tested', 'Staff walkthrough'],
    form: 'Installation check',
    doneNote: 'Installed · signed',
    question: 'What does error E34 mean?',
    answer: 'E34 means the steam generator needs descaling. Run the descale programme from the cleaning menu.',
    source: 'User manual p.28',
    section: '9.4 Error codes',
    excerpt: 'E34: steam generator scale. Run the descale programme.',
    part: 'Door seal',
    issue: 'Door seal leaking steam',
    parts: [
      ['Door seal', 'FN-DS10-2411', 'L24-0411'],
      ['Core probe', 'FN-CP-2408', 'L24-0408'],
      ['Fan motor', 'FN-FM10-2402', 'L24-0402'],
    ],
    history: [
      ['Installed', 'APR 2024'],
      ['Service · 6 months', 'OCT 2024'],
      ['Gas safety check', 'APR 2025'],
    ],
    serviceNote: '2 visits, gas check in date',
    since: 'Active since installation',
  },
  'industrial-precision': {
    brand: 'Lotwerk',
    model: 'FL-316',
    kind: 'Flange',
    title: 'Flange 316L, qty 40',
    serial: 'LW-D24118-01',
    lot: 'L-24-0918',
    shipTo: 'Halden Pumps BV',
    partner: 'Sold direct',
    partnerRole: 'distributor',
    owner: 'Halden Pumps BV',
    owner2: 'Halden Service',
    checks: ['Quantity counted', 'Lot matches slip', '3.1 certificate attached', 'Packaging intact', 'Goods-in signed'],
    form: 'Goods-in check',
    question: 'Which heat number is this lot from?',
    answer: 'Lot L-24-0918 is heat 7731-B. The 3.1 certificate for that heat is attached to this delivery.',
    source: 'Inspection certificate 3.1',
    section: 'Heat number',
    excerpt: 'Heat 7731-B. EN 10204 3.1.',
    part: 'Flange',
    issue: '38 received, 40 on the slip',
    parts: [
      ['Gasket set', 'LW-GS-24-118', 'L-24-0902'],
      ['Bolt kit M16', 'LW-BK-24-077', 'L-24-0877'],
      ['3.1 certificate', 'CERT-7731-B', 'L-24-0918'],
    ],
    statusRow: ['Delivery', 'Shipped', 'Received'],
    serviceNote: 'No earlier issues on this lot',
    since: 'Delivery D-24118',
  },
  'premium-durables': {
    brand: 'Aldwyn Audio',
    model: 'A-40',
    kind: 'Amplifier',
    serial: 'AA40-2019-07733',
    lot: 'L19-0733',
    partner: 'Hartley Hi-Fi',
    partnerRole: 'retailer',
    owner: 'Tom Reid',
    owner2: 'Ana Costa',
    checks: ['Unit inspected', 'Mains lead and fuse', 'Remote paired', 'Speaker wiring checked', 'Owner walkthrough'],
    form: 'Setup and registration',
    question: 'Which fuse does the rear panel take?',
    answer: 'T3.15A slow-blow, 20 mm. Switch off and unplug before you open the fuse holder.',
    source: 'Owner manual p.9',
    section: '2.4 Mains fuse',
    excerpt: 'T3.15A slow-blow, 20 mm.',
    part: 'Volume encoder',
    issue: 'Volume knob skipping steps',
    parts: [
      ['Volume encoder', 'AA-VE-19-221', 'L19-07'],
      ['Remote handset', 'AA-RC-19-090', 'L19-06'],
      ['Feet set', 'AA-FT-19-014', 'L19-05'],
    ],
    history: [
      ['Sold · Hartley Hi-Fi', 'JUN 2019'],
      ['Service · bias check', 'MAY 2022'],
      ['Encoder replaced', 'FEB 2025'],
    ],
    serviceNote: '2 services, genuine parts',
    since: 'Registered, transferable',
  },
  'medical-laboratory-instruments': {
    brand: 'Brennock',
    model: 'CR-4',
    kind: 'Benchtop centrifuge',
    serial: 'BRK-CR4-25-01376',
    lot: 'L2503-11',
    partner: 'Penmore Scientific',
    partnerRole: 'distributor',
    owner: 'Kingsmere Pathology',
    owner2: 'Larkfield Research',
    checks: ['Speed verified', 'Temperature verified', 'Lid lock tested', 'Imbalance cut-out tested', 'Electrical safety test'],
    form: 'PPM and calibration',
    signLabel: 'Service engineer',
    signer: 'Penmore Scientific',
    outcome: ['Certificate filed', 'on this serial'],
    statusRow: ['Calibration', 'Due', 'In date until February'],
    doneNote: 'PPM 5 of 5 · signed',
    question: 'The lid will not open after a run. What do I do?',
    answer: 'Wait for the rotor to stop fully. If the lid stays locked, switch off at the mains and use the emergency lid release on the right-hand side.',
    source: 'Operating manual p.31',
    section: '6.2 Emergency lid release',
    excerpt: 'Switch off, wait for the rotor to stop, then use the release.',
    part: 'Lid seal',
    issue: 'Chamber not holding set temperature',
    parts: [
      ['Lid seal', 'BRK-LS4-25-031', 'L2502-07'],
      ['Swing-out rotor', 'BRK-R4S-25-118', 'L2501-22'],
      ['Air filter', 'BRK-AF4-25-009', 'L2503-02'],
    ],
    history: [
      ['Installed · Penmore Scientific', 'MAR 2025'],
      ['PPM and calibration', 'SEP 2025'],
      ['PPM and calibration', 'MAR 2026'],
    ],
    serviceNote: '2 PPM visits, both signed',
    since: 'Calibration in date',
  },
  'sports-fitness-equipment': {
    brand: 'Strideworth',
    model: 'T9',
    kind: 'Treadmill',
    serial: 'SW-T9-24-20871',
    lot: 'L2409-05',
    partner: 'Kellaway Fitness',
    partnerRole: 'service partner',
    owner: 'Harbourside Hotel',
    owner2: 'Foundry Lane Gym',
    checks: ['Belt tracked and tensioned', 'Deck and rollers inspected', 'Emergency stop tested', 'Handrails and fixings checked', 'Console error log read'],
    form: 'Service and safety inspection',
    signLabel: 'Service engineer',
    signer: 'Kellaway Fitness',
    outcome: ['Visit filed', 'on this machine'],
    statusRow: ['Service', 'Due', 'Done today'],
    doneNote: 'Service 5 of 5 · signed',
    question: 'The console shows E07. What does it mean?',
    answer: 'E07 means the speed sensor has lost its signal. Switch off, check the sensor lead at the front roller, then restart. If it comes back, book a visit.',
    source: 'Service manual p.44',
    section: '8.2 Error codes',
    excerpt: 'E07: speed sensor signal lost.',
    part: 'Speed sensor',
    issue: 'E07 returns after restart',
    parts: [
      ['Running belt', 'SW-RB9-24-114', 'L2408-12'],
      ['Speed sensor', 'SW-SS9-24-207', 'L2409-01'],
      ['Drive motor', 'SW-DM9-24-031', 'L2407-19'],
    ],
    history: [
      ['Installed · Kellaway Fitness', 'SEP 2024'],
      ['Service · quarterly', 'DEC 2024'],
      ['Service · quarterly', 'MAR 2025'],
    ],
    serviceNote: '2 visits, inspections signed',
    since: 'Under service contract',
  },
};
